import {Alert, Button, Card, Radio} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  WhatsAppSignupFailureReason,
  WhatsAppSignupScenario,
  WhatsAppSignupScenarioSchema,
} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {
  useWhatsAppEmbeddedSignup,
  WhatsAppSignupOutcome,
} from '../../hooks/useWhatsAppEmbeddedSignup';

// Coexistence keeps the number working in the WhatsApp Business app. Standard onboarding of a number
// that is already in that app moves it away from the phone, so it is the deliberate choice.
const DEFAULT_SCENARIO: WhatsAppSignupScenario = 'coexistence';

const FAILURE_MESSAGE_KEYS: Record<WhatsAppSignupFailureReason, string> = {
  code_exchange_failed: 'page.settings.whatsApp.connection.failure.codeExchangeFailed',
  account_not_granted: 'page.settings.whatsApp.connection.failure.accountNotGranted',
  phone_number_not_in_account: 'page.settings.whatsApp.connection.failure.phoneNumberNotInAccount',
  phone_number_not_selected: 'page.settings.whatsApp.connection.failure.phoneNumberNotSelected',
  account_connected_elsewhere:
    'page.settings.whatsApp.connection.failure.accountConnectedElsewhere',
  another_account_connected: 'page.settings.whatsApp.connection.failure.anotherAccountConnected',
  webhook_subscription_failed:
    'page.settings.whatsApp.connection.failure.webhookSubscriptionFailed',
  phone_number_registration_failed:
    'page.settings.whatsApp.connection.failure.phoneNumberRegistrationFailed',
  meta_request_failed: 'page.settings.whatsApp.connection.failure.metaRequestFailed',
};

const getOutcomeMessage = (outcome: WhatsAppSignupOutcome | null) =>
  match(outcome)
    .with(null, () => undefined)
    .with({state: 'cancelled'}, () => i18n.t('page.settings.whatsApp.connection.cancelled'))
    .with({state: 'expired'}, () => i18n.t('page.settings.whatsApp.connection.expired'))
    .with({state: 'failed'}, ({failureReason}) =>
      i18n.t(FAILURE_MESSAGE_KEYS[failureReason ?? 'meta_request_failed'])
    )
    .exhaustive();

interface WhatsAppConnectProps {
  /** Set when an existing connection is being replaced; the user can return to it. */
  onBack?: () => void;
  onConnected?: () => void;
}

export function WhatsAppConnect(props: WhatsAppConnectProps) {
  const [scenario, setScenario] = useState<WhatsAppSignupScenario>(DEFAULT_SCENARIO);
  const {connect, outcome, isConnecting} = useWhatsAppEmbeddedSignup(props.onConnected);

  return (
    <Card title={i18n.t('page.settings.whatsApp.connection.connectTitle')} variant="inlineGrey">
      <VStack spacing={4}>
        <Text size="small">{i18n.t('page.settings.whatsApp.connection.connectDescription')}</Text>
        <Show when={isNotNil(props.onBack)}>
          <Text size="small">
            {i18n.t('page.settings.whatsApp.connection.reconnectDescription')}
          </Text>
        </Show>
        <Radio
          label={i18n.t('page.settings.whatsApp.connection.scenario')}
          value={scenario}
          onChange={(value) => {
            const parsed = WhatsAppSignupScenarioSchema.safeParse(value);
            if (parsed.success) {
              setScenario(parsed.data);
            }
          }}
          direction="column"
          spacing={2}
          isDisabled={isConnecting}
          options={[
            {
              value: 'coexistence',
              label: i18n.t('page.settings.whatsApp.connection.scenarioCoexistence'),
            },
            {
              value: 'standard',
              label: i18n.t('page.settings.whatsApp.connection.scenarioStandard'),
            },
          ]}
        />
        <Show when={isNotNil(outcome)}>
          <Alert
            variant={outcome?.state === 'failed' ? 'error' : 'warning'}
            title={getOutcomeMessage(outcome)}
            message={i18n.t('page.settings.whatsApp.connection.retryHint')}
          />
        </Show>
        <HStack spacing={2}>
          <Button
            variant="primary"
            title={
              isNil(outcome)
                ? i18n.t('page.settings.whatsApp.connection.connect')
                : i18n.t('general.actions.tryAgain')
            }
            isLoading={isConnecting}
            // isLoading alone keeps the button focusable, so Enter would start a second signup.
            isDisabled={isConnecting}
            onClick={() => connect(scenario)}
          />
          <Show when={isNotNil(props.onBack)}>
            <Button
              variant="secondary"
              title={i18n.t('general.actions.back')}
              isDisabled={isConnecting}
              onClick={props.onBack}
            />
          </Show>
        </HStack>
      </VStack>
    </Card>
  );
}
